/**
 * opp-detector REST：跨所价差 TopN
 */

import { API_CONFIG } from '@/config/api';
import { apiFetch } from '@/lib/apiFetch';
import type { CandleDataPoint, CandleResponse, DetailedSpreadResponse } from '@/services/spreadApi';

export interface OppSpreadItem {
  symbol: string;
  category?: string;
  buy_exchange: string;
  sell_exchange: string;
  buy_inst_id: string;
  sell_inst_id: string;
  ask_buy: number;
  bid_sell: number;
  spread: number;
  ts_us: number;
  open_line?: number | null;
  open_line_ratio?: number | null;
  sort_score?: number;
}

export type RealtimeSpreadDirection = 'long1_short2' | 'short1_long2';

export interface RealtimeSpreadWsParams {
  exchange1: string;
  inst1: string;
  exchange2: string;
  inst2: string;
  interval: number;
  direction: RealtimeSpreadDirection;
  ticket?: string;
}

export interface RealtimeSpreadSnapshotMessage {
  type: 'snapshot';
  interval: number;
  direction: RealtimeSpreadDirection;
  generated_at_ms: number;
}

export interface RealtimeSpreadTickMessage {
  type: 'tick';
  ts: number;
  long1_short2_open_spread: number;
  long1_short2_close_spread: number;
  short1_long2_open_spread: number;
  short1_long2_close_spread: number;
}

export interface RealtimeIndexMetricsMessage {
  type: 'index_metrics';
  ts: number;
  bias_exchange1: number;
  bias_exchange2: number;
  long1_short2_index_spread: number;
  short1_long2_index_spread: number;
  long1_short2_adjusted_spread: number;
  short1_long2_adjusted_spread: number;
  long1_short2_adjusted_spread_ma_1m: number;
  long1_short2_adjusted_spread_ma_5m: number;
  long1_short2_adjusted_spread_ma_10m: number;
  long1_short2_adjusted_spread_ma_60m: number;
  short1_long2_adjusted_spread_ma_1m: number;
  short1_long2_adjusted_spread_ma_5m: number;
  short1_long2_adjusted_spread_ma_10m: number;
  short1_long2_adjusted_spread_ma_60m: number;
  long1_short2_orderbook_spread: number;
  short1_long2_orderbook_spread: number;
  index_price1: number;
  index_price2: number;
  orderbook_mid1: number;
  orderbook_mid2: number;
}

export interface RealtimeKlineUpdateMessage {
  type: 'kline_update';
  source: 'exchange1' | 'exchange2';
  exchange: string;
  inst_id: string;
  ts: number;
  open: number;
  high: number;
  low: number;
  close: number;
  tick_count: number;
  closed: boolean;
}

export interface RealtimeHeartbeatMessage {
  type: 'heartbeat';
  ts: number;
}

export type RealtimeSpreadWsMessage =
  | RealtimeSpreadSnapshotMessage
  | RealtimeSpreadTickMessage
  | RealtimeIndexMetricsMessage
  | RealtimeKlineUpdateMessage
  | RealtimeHeartbeatMessage;

export interface WsTicketResponse {
  ticket: string;
  expires_at_ms: number;
}

export type OppSpreadTopSortBy = 'spread' | 'abs_spread' | 'relative_open_line';

export type OppSpreadPairsSortBy =
  | 'volatility_std'
  | 'volatility_range'
  | 'volatility_mad'
  | 'volatility_cv'
  | 'mid_spread_deviation';

export interface OppSpreadPairItem {
  symbol: string;
  category?: string;
  exchange1: string;
  exchange2: string;
  inst_id1: string;
  inst_id2: string;
  spread: number;
  ts_us: number;
  sort_score: number;
  sample_count: number;
  global_median_mid_spread?: number | null;
  deviation_count?: number | null;
  deviation_ratio?: number | null;
  deviation_pct?: number | null;
}

export interface OppSpreadPairsResponse {
  exchange1: string;
  exchange2: string;
  window_secs: number;
  sort_by: string;
  deviation_pct?: number | null;
  total: number;
  offset: number;
  limit: number;
  items: OppSpreadPairItem[];
}

export interface OppSpreadPairsQuery {
  exchange1: string;
  exchange2: string;
  inst_id1?: string;
  inst_id2?: string;
  window_secs?: number;
  sort_by?: OppSpreadPairsSortBy;
  deviation_pct?: number;
  interval?: number;
  limit?: number;
  offset?: number;
}

export type OppMarketScreenerSortBy =
  | 'price_change_pct'
  | 'price_range_pct'
  | 'price_volatility_std'
  | 'price_volatility_mad'
  | 'volume_usdt'
  | 'volume_ratio';

export interface OppMarketScreenerItem {
  symbol: string;
  inst_id: string;
  category?: string;
  close: number;
  price_change_pct: number;
  price_range_pct: number;
  volume_usdt: number;
  volume_ratio: number;
  sort_score: number;
  ts_ms: number;
}

export interface OppMarketScreenerResponse {
  exchange: string;
  window_secs: number;
  sort_by: string;
  total: number;
  offset: number;
  limit: number;
  items: OppMarketScreenerItem[];
}

export interface OppMarketScreenerQuery {
  exchange: string;
  window_secs?: number;
  sort_by?: OppMarketScreenerSortBy;
  min_volume_usdt?: number;
  limit?: number;
  offset?: number;
}

export interface OrderbookExchangeItem {
  name: string;
  exchange: string;
  enabled: boolean;
  obs_port?: number | null;
}

export interface OppCommonSymbolItem {
  symbol: string;
  exchange1: string;
  exchange2: string;
  inst_id1: string;
  inst_id2: string;
  inst_id_code1?: number | null;
  inst_id_code2?: number | null;
  category?: string;
}

export interface OppCommonSymbolsResponse {
  exchange1: string;
  exchange2: string;
  count: number;
  symbols: OppCommonSymbolItem[];
}

export const OPP_DETECTOR_DEFAULT_EXCHANGE1_FEE = 0.0005;
export const OPP_DETECTOR_DEFAULT_EXCHANGE2_FEE = 0.00055;
export const OPP_DETECTOR_DEFAULT_MIN_PROFIT_PERCENTAGE = 0.005;

export const OPP_DETECTOR_DEFAULT_DEVIATION_CYCLE_ENTRY_THRESHOLD = 0.005;
export const OPP_DETECTOR_DEFAULT_DEVIATION_CYCLE_EXIT_THRESHOLD = 0.002;
export const OPP_DETECTOR_DEFAULT_DEVIATION_CYCLE_CONFIRM_ENTRY_POINTS = 1;
export const OPP_DETECTOR_DEFAULT_DEVIATION_CYCLE_CONFIRM_EXIT_POINTS = 3;

export interface OppDeviationCycleConfig {
  entryThreshold: number;
  exitThreshold: number;
  confirmEntryPoints: number;
  confirmExitPoints: number;
}

export const defaultOppDeviationCycleConfig = (): OppDeviationCycleConfig => ({
  entryThreshold: OPP_DETECTOR_DEFAULT_DEVIATION_CYCLE_ENTRY_THRESHOLD,
  exitThreshold: OPP_DETECTOR_DEFAULT_DEVIATION_CYCLE_EXIT_THRESHOLD,
  confirmEntryPoints: OPP_DETECTOR_DEFAULT_DEVIATION_CYCLE_CONFIRM_ENTRY_POINTS,
  confirmExitPoints: OPP_DETECTOR_DEFAULT_DEVIATION_CYCLE_CONFIRM_EXIT_POINTS,
});

export interface OppSpreadQuery {
  exchange1: string;
  inst_id1: string;
  exchange2: string;
  inst_id2: string;
  start_ms: number;
  end_ms: number;
  interval: number;
  sma_period?: number;
  exchange1_fee?: number;
  exchange2_fee?: number;
  min_profit_percentage?: number;
  fill_miss_data?: boolean;
}

export interface OppL1CandlesQuery {
  exchange: string;
  inst_id: string;
  start_ms: number;
  end_ms: number;
  interval?: number;
}

export interface OppFundingRateSample {
  exchange: string;
  symbol: string;
  inst_id: string;
  rate: number;
  funding_time_ms: number | null;
  ts_ms: number;
}

export interface OppResampledFundingPoint {
  ts_ms: number;
  rate: number;
  funding_time_ms: number | null;
}

export interface OppFundingHistoryQuery {
  exchange: string;
  inst_id: string;
  start_ms: number;
  end_ms: number;
  interval?: number;
}

export interface OppFundingHistoryResponse {
  exchange: string;
  inst_id: string;
  stored_interval_secs: number;
  query_start_ms: number;
  query_end_ms: number;
  sample_count: number;
  current_rate: number | null;
  next_funding_time_ms: number | null;
  resampled_interval_secs?: number;
  resampled?: OppResampledFundingPoint[];
  samples: OppFundingRateSample[];
}

export type OppFundingHedgeSortBy = 'abs_net_desc' | 'abs_net_asc' | 'net_desc' | 'net_asc';

export interface OppFundingHedgeQuery {
  exchange1: string;
  exchange2: string;
  start_ms: number;
  end_ms?: number;
  sort_by?: OppFundingHedgeSortBy;
  limit?: number;
  offset?: number;
}

export interface OppFundingHedgeItem {
  symbol: string;
  inst_id1: string;
  inst_id2: string;
  direction: 'long1_short2' | 'short1_long2';
  net_funding_total: number;
  abs_net_funding_total: number;
  long1_short2_net: number;
  short1_long2_net: number;
  annualized_apy: number;
  settlement_event_count: number;
  settlement_count1: number;
  settlement_count2: number;
}

export interface OppFundingHedgeResponse {
  exchange1: string;
  exchange2: string;
  query_start_ms: number;
  query_end_ms: number;
  total_pairs: number;
  count: number;
  items: OppFundingHedgeItem[];
}

export type OppFundingHedgeUpcomingSortBy =
  | 'abs_net_desc'
  | 'abs_net_asc'
  | 'net_desc'
  | 'net_asc'
  | 'time_asc';

export interface OppFundingHedgeUpcomingQuery {
  exchange1: string;
  exchange2: string;
  sort_by?: OppFundingHedgeUpcomingSortBy;
  limit?: number;
  offset?: number;
}

export interface OppFundingHedgeUpcomingItem {
  symbol: string;
  inst_id1: string;
  inst_id2: string;
  direction: 'long1_short2' | 'short1_long2';
  next_net_funding: number;
  abs_next_net_funding: number;
  long1_short2_net: number;
  short1_long2_net: number;
  next_settlement_time_ms: number;
  next_settlement_kind: 'both' | 'leg1_only' | 'leg2_only';
  ms_to_next_settlement: number;
  leg1_rate: number;
  leg2_rate: number;
  leg1_next_funding_time_ms: number;
  leg2_next_funding_time_ms: number;
}

export interface OppFundingHedgeUpcomingResponse {
  exchange1: string;
  exchange2: string;
  now_ms: number;
  total_pairs: number;
  count: number;
  items: OppFundingHedgeUpcomingItem[];
}

export type OppFundingHedgeScreenSortBy =
  | 'abs_next_net_desc'
  | 'abs_next_net_asc'
  | 'next_net_desc'
  | 'next_net_asc'
  | 'next_time_asc'
  | 'abs_hist_net_desc'
  | 'abs_hist_net_asc';

export interface OppFundingHedgeScreenQuery {
  exchanges: string[];
  history_window_secs?: number;
  sort_by?: OppFundingHedgeScreenSortBy;
  limit?: number;
  offset?: number;
}

export interface OppFundingUpcomingSnapshot {
  direction: 'long1_short2' | 'short1_long2';
  next_net_funding: number;
  abs_next_net_funding: number;
  long1_short2_net: number;
  short1_long2_net: number;
  next_settlement_time_ms: number;
  next_settlement_kind: 'both' | 'leg1_only' | 'leg2_only';
  ms_to_next_settlement: number;
  leg1_rate: number;
  leg2_rate: number;
  leg1_next_funding_time_ms: number;
  leg2_next_funding_time_ms: number;
}

export interface OppFundingHistoricalSnapshot {
  direction: 'long1_short2' | 'short1_long2';
  net_funding_total: number;
  abs_net_funding_total: number;
  long1_short2_net: number;
  short1_long2_net: number;
  annualized_apy: number;
  settlement_event_count: number;
  settlement_count1: number;
  settlement_count2: number;
}

export interface OppFundingHedgeScreenItem {
  exchange1: string;
  exchange2: string;
  symbol: string;
  inst_id1: string;
  inst_id2: string;
  upcoming: OppFundingUpcomingSnapshot;
  historical?: OppFundingHistoricalSnapshot | null;
}

export interface OppFundingHedgeScreenResponse {
  exchanges: string[];
  exchange_pair_count: number;
  now_ms: number;
  history_window_secs: number;
  history_start_ms: number;
  history_end_ms: number;
  total_pairs: number;
  count: number;
  items: OppFundingHedgeScreenItem[];
}

interface OppL1CandlePoint {
  timestamp: string;
  open: number;
  high: number;
  low: number;
  close: number;
  tick_count: number;
}

interface OppL1CandlesResponse {
  candles: OppL1CandlePoint[];
}

function oppDetectorUrl(path: string): string {
  const base = API_CONFIG.OPP_DETECTOR_PATH_PREFIX;
  return base ? `${base}${path}` : path;
}

function spreadsTopUrl(
  limit: number,
  second?: number,
  sortBy?: OppSpreadTopSortBy,
  exchanges?: string[],
): string {
  const params = new URLSearchParams({
    top: String(limit),
  });
  if (Number.isFinite(second) && (second ?? 0) > 0) {
    params.set('second', String(Math.floor(second as number)));
  }
  if (sortBy) params.set('sort_by', sortBy);
  if (exchanges?.length) {
    params.set('exchanges', exchanges.map((item) => item.trim()).filter(Boolean).join(','));
  }
  const path = `/api/opp-detector/spreads/top?${params.toString()}`;
  const base = API_CONFIG.OPP_DETECTOR_PATH_PREFIX;
  if (!base) return path;
  return `${base}${path}`;
}

export async function getOppSpreadsTop(
  limit = 100,
  second?: number,
  sortBy?: OppSpreadTopSortBy,
  exchanges?: string[],
): Promise<OppSpreadItem[]> {
  const res = await apiFetch(spreadsTopUrl(limit, second, sortBy, exchanges));
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`opp-detector ${res.status}: ${text || res.statusText}`);
  }
  const data: unknown = await res.json();
  if (!Array.isArray(data)) {
    throw new Error('opp-detector: expected JSON array');
  }
  return data as OppSpreadItem[];
}

export async function getOppSpreadsPairs(params: OppSpreadPairsQuery): Promise<OppSpreadPairsResponse> {
  const q = new URLSearchParams({
    exchange1: params.exchange1,
    exchange2: params.exchange2,
  });
  if (params.inst_id1?.trim()) q.set('inst_id1', params.inst_id1.trim());
  if (params.inst_id2?.trim()) q.set('inst_id2', params.inst_id2.trim());
  if (params.window_secs != null && params.window_secs > 0) {
    q.set('window_secs', String(Math.floor(params.window_secs)));
  }
  if (params.sort_by) q.set('sort_by', params.sort_by);
  if (params.deviation_pct != null && Number.isFinite(params.deviation_pct)) {
    q.set('deviation_pct', String(params.deviation_pct));
  }
  if (params.interval != null && params.interval > 0) {
    q.set('interval', String(Math.floor(params.interval)));
  }
  if (params.limit != null && params.limit > 0) q.set('limit', String(Math.floor(params.limit)));
  if (params.offset != null && params.offset >= 0) q.set('offset', String(Math.floor(params.offset)));

  const res = await apiFetch(`${oppDetectorUrl('/api/opp-detector/spreads/pairs')}?${q.toString()}`);
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`opp-detector pairs ${res.status}: ${text || res.statusText}`);
  }
  return res.json() as Promise<OppSpreadPairsResponse>;
}

export async function getOppMarketScreener(
  params: OppMarketScreenerQuery,
): Promise<OppMarketScreenerResponse> {
  const q = new URLSearchParams({ exchange: params.exchange });
  if (params.window_secs != null && params.window_secs > 0) {
    q.set('window_secs', String(Math.floor(params.window_secs)));
  }
  if (params.sort_by) q.set('sort_by', params.sort_by);
  if (params.min_volume_usdt != null && params.min_volume_usdt > 0) {
    q.set('min_volume_usdt', String(params.min_volume_usdt));
  }
  if (params.limit != null && params.limit > 0) q.set('limit', String(Math.floor(params.limit)));
  if (params.offset != null && params.offset >= 0) q.set('offset', String(Math.floor(params.offset)));

  const res = await apiFetch(`${oppDetectorUrl('/api/opp-detector/market/screener')}?${q.toString()}`);
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`opp-detector screener ${res.status}: ${text || res.statusText}`);
  }
  return res.json() as Promise<OppMarketScreenerResponse>;
}

export interface OppInstrumentInfo {
  exchange: string;
  inst_id: string;
  symbol: string;
  category?: string;
  /** Funding settlement interval in hours (converted from instrument minutes on the server). */
  funding_interval_hours?: number | null;
  upper_funding_rate?: number | null;
  lower_funding_rate?: number | null;
  index_components?: Record<string, unknown> | null;
}

export async function getOppInstrumentInfo(exchange: string, instId: string): Promise<OppInstrumentInfo | null> {
  const q = new URLSearchParams({ exchange, inst_id: instId });
  const res = await apiFetch(`${oppDetectorUrl('/api/opp-detector/instruments/info')}?${q.toString()}`);
  if (res.status === 404) return null;
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`opp-detector instrument info ${res.status}: ${text || res.statusText}`);
  }
  return (await res.json()) as OppInstrumentInfo;
}

export type PairRiskDataConfidence = 'high' | 'low' | 'insufficient';

export interface PairRiskFactorContribution {
  id: string;
  raw_score: number;
  weight: number;
  weighted_score: number;
  attribution_pct: number;
  details: Record<string, unknown>;
}

export interface PairRiskDirectionReport {
  direction: RealtimeSpreadDirection;
  buy_exchange: string;
  sell_exchange: string;
  risk_level: number;
  raw_score: number;
  factors: PairRiskFactorContribution[];
}

export interface PairRiskReport {
  symbol: string;
  exchange1: string;
  exchange2: string;
  inst_id1: string;
  inst_id2: string;
  evaluated_at_ms: number;
  data_confidence: PairRiskDataConfidence;
  spread_points: number;
  directions: PairRiskDirectionReport[];
}

export async function getOppPairRiskEvaluate(params: {
  symbol: string;
  exchange1: string;
  exchange2: string;
  inst_id1?: string;
  inst_id2?: string;
}): Promise<PairRiskReport | null> {
  const q = new URLSearchParams({
    symbol: params.symbol,
    exchange1: params.exchange1,
    exchange2: params.exchange2,
  });
  if (params.inst_id1) q.set('inst_id1', params.inst_id1);
  if (params.inst_id2) q.set('inst_id2', params.inst_id2);

  const res = await apiFetch(`${oppDetectorUrl('/api/opp-detector/pair-risk/evaluate')}?${q.toString()}`);
  if (res.status === 404) return null;
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`opp-detector pair-risk ${res.status}: ${text || res.statusText}`);
  }
  return (await res.json()) as PairRiskReport;
}

export async function getOppCommonSymbols(
  exchange1: string,
  exchange2: string,
): Promise<OppCommonSymbolsResponse> {
  const q = new URLSearchParams({
    exchange1,
    exchange2,
  });
  const res = await apiFetch(
    `${oppDetectorUrl('/api/opp-detector/instruments/common-symbols')}?${q.toString()}`,
  );
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`opp-detector common-symbols ${res.status}: ${text || res.statusText}`);
  }
  return res.json() as Promise<OppCommonSymbolsResponse>;
}

export type OppShort1Long2MidDeviationSortBy =
  | 'max_abs_deviation'
  | 'exceed_0_002_ratio'
  | 'exceed_0_005_ratio'
  | 'exceed_0_01_ratio';

export interface OppShort1Long2MidDeviationItem {
  symbol: string;
  category?: string;
  exchange1: string;
  exchange2: string;
  inst_id1: string;
  inst_id2: string;
  sort_score: number;
  stats: {
    total_points: number;
    exceed_0_002_ratio: number;
    exceed_0_005_ratio: number;
    exceed_0_01_ratio: number;
    max_abs_deviation: number;
  };
}

export interface OppShort1Long2MidDeviationResponse {
  exchange1: string;
  exchange2: string;
  start_ms: number;
  end_ms: number;
  interval: number;
  sort_by: string;
  total: number;
  offset: number;
  limit: number;
  items: OppShort1Long2MidDeviationItem[];
}

export interface OppShort1Long2MidDeviationQuery {
  exchange1: string;
  exchange2: string;
  window_secs?: number;
  start_ms?: number;
  end_ms?: number;
  interval?: number;
  sort_by?: OppShort1Long2MidDeviationSortBy;
  limit?: number;
  offset?: number;
}

export async function getOppShort1Long2MidDeviation(
  params: OppShort1Long2MidDeviationQuery,
): Promise<OppShort1Long2MidDeviationResponse> {
  const q = new URLSearchParams({
    exchange1: params.exchange1,
    exchange2: params.exchange2,
  });
  if (params.window_secs != null && params.window_secs > 0) {
    q.set('window_secs', String(Math.floor(params.window_secs)));
  }
  if (params.start_ms != null) q.set('start_ms', String(Math.floor(params.start_ms)));
  if (params.end_ms != null) q.set('end_ms', String(Math.floor(params.end_ms)));
  if (params.interval != null && params.interval > 0) {
    q.set('interval', String(Math.floor(params.interval)));
  }
  if (params.sort_by) q.set('sort_by', params.sort_by);
  if (params.limit != null && params.limit > 0) q.set('limit', String(Math.floor(params.limit)));
  if (params.offset != null && params.offset >= 0) q.set('offset', String(Math.floor(params.offset)));

  const res = await apiFetch(
    `${oppDetectorUrl('/api/opp-detector/spreads/short1-long2-mid-deviation')}?${q.toString()}`,
  );
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`opp-detector short1-long2-mid-deviation ${res.status}: ${text || res.statusText}`);
  }
  return res.json() as Promise<OppShort1Long2MidDeviationResponse>;
}

const DEFAULT_OPP_TOP_EXCHANGES = ['OKX', 'Binance', 'Bybit'];

export function defaultOppTopExchanges(available: string[]): string[] {
  const defaults = DEFAULT_OPP_TOP_EXCHANGES.filter((exchange) => available.includes(exchange));
  if (defaults.length > 0) return defaults;
  return available.slice(0, Math.min(3, available.length));
}

export async function getOppOrderbookExchanges(): Promise<string[]> {
  const res = await apiFetch(oppDetectorUrl('/api/opp-detector/orderbook-exchanges'));
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`opp-detector exchanges ${res.status}: ${text || res.statusText}`);
  }
  const data: unknown = await res.json();
  if (!Array.isArray(data)) {
    throw new Error('opp-detector: expected exchanges array');
  }
  return (data as OrderbookExchangeItem[])
    .filter((item) => item.enabled)
    .map((item) => item.exchange);
}

export interface OppSpreadDeviationCyclePointSnapshot {
  spread: number;
  baseline: number;
  deviation: number;
}

export interface OppSpreadDeviationCycle {
  cycle_index: number;
  start_ms: number;
  end_ms: number;
  duration_ms: number;
  start_timestamp: string;
  end_timestamp: string;
  peak_deviation: number;
  peak_ms: number;
  peak_timestamp: string;
  peak_spread: number;
  peak_baseline: number;
  start: OppSpreadDeviationCyclePointSnapshot;
  end: OppSpreadDeviationCyclePointSnapshot;
  point_count: number;
  deviant_point_count: number;
  deviant_point_ratio: number;
  false_reversion_count: number;
}

export type OppOpenSpreadDeviationCycleState = 'deviant' | 'reverting';

export interface OppOpenSpreadDeviationCycle {
  start_ms: number;
  start_timestamp: string;
  start: OppSpreadDeviationCyclePointSnapshot;
  peak_deviation: number;
  peak_ms: number;
  peak_timestamp: string;
  state: OppOpenSpreadDeviationCycleState;
  reversion_streak: number;
  points_until_confirmed: number;
  elapsed_ms: number;
  point_count: number;
}

export interface OppSpreadDeviationCycleSummary {
  total_points: number;
  completed_cycle_count: number;
  has_open_cycle: boolean;
  avg_duration_ms: number | null;
  max_duration_ms: number | null;
  max_peak_deviation: number | null;
}

export interface OppSpreadDeviationCycleConfig {
  entry_threshold: number;
  exit_threshold: number;
  confirm_entry_points: number;
  confirm_exit_points: number;
  positive_only: boolean;
  baseline: 'point_median' | 'global_median';
}

export interface OppSpreadDeviationCyclesResponse {
  exchange1: string;
  exchange2: string;
  inst_id1: string;
  inst_id2: string;
  start_ms: number;
  end_ms: number;
  interval_secs: number;
  spread_side: string;
  config: OppSpreadDeviationCycleConfig;
  summary: OppSpreadDeviationCycleSummary;
  cycles: OppSpreadDeviationCycle[];
  open_cycle?: OppOpenSpreadDeviationCycle | null;
}

export interface OppSpreadDeviationCyclesQuery extends OppSpreadQuery {
  spread_side?: RealtimeSpreadDirection;
  entry_threshold?: number;
  exit_threshold?: number;
  confirm_entry_points?: number;
  confirm_exit_points?: number;
  positive_only?: boolean;
  baseline?: 'point_median' | 'global_median';
  include_open_cycle?: boolean;
}

export async function getOppSpreadDeviationCycles(
  params: OppSpreadDeviationCyclesQuery,
): Promise<OppSpreadDeviationCyclesResponse> {
  const q = new URLSearchParams();
  q.set('exchange1', params.exchange1);
  q.set('inst_id1', params.inst_id1);
  q.set('exchange2', params.exchange2);
  q.set('inst_id2', params.inst_id2);
  q.set('start_ms', String(params.start_ms));
  q.set('end_ms', String(params.end_ms));
  q.set('interval', String(params.interval));
  if (params.sma_period != null) q.set('sma_period', String(params.sma_period));
  q.set('exchange1_fee', String(params.exchange1_fee ?? OPP_DETECTOR_DEFAULT_EXCHANGE1_FEE));
  q.set('exchange2_fee', String(params.exchange2_fee ?? OPP_DETECTOR_DEFAULT_EXCHANGE2_FEE));
  q.set(
    'min_profit_percentage',
    String(params.min_profit_percentage ?? OPP_DETECTOR_DEFAULT_MIN_PROFIT_PERCENTAGE),
  );
  if (params.fill_miss_data != null) q.set('fill_miss_data', String(params.fill_miss_data));
  if (params.spread_side) q.set('spread_side', params.spread_side);
  if (params.entry_threshold != null) q.set('entry_threshold', String(params.entry_threshold));
  if (params.exit_threshold != null) q.set('exit_threshold', String(params.exit_threshold));
  if (params.confirm_entry_points != null) {
    q.set('confirm_entry_points', String(params.confirm_entry_points));
  }
  if (params.confirm_exit_points != null) {
    q.set('confirm_exit_points', String(params.confirm_exit_points));
  }
  if (params.positive_only != null) q.set('positive_only', String(params.positive_only));
  if (params.baseline) q.set('baseline', params.baseline);
  if (params.include_open_cycle != null) {
    q.set('include_open_cycle', String(params.include_open_cycle));
  }
  q.set('use_cache', 'false');

  const res = await apiFetch(`${oppDetectorUrl('/api/opp-detector/spread/deviation-cycles')}?${q.toString()}`);
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`opp-detector spread deviation-cycles ${res.status}: ${text || res.statusText}`);
  }
  return res.json() as Promise<OppSpreadDeviationCyclesResponse>;
}

export async function getOppDetailedSpread(params: OppSpreadQuery): Promise<DetailedSpreadResponse> {
  const q = new URLSearchParams();
  q.set('exchange1', params.exchange1);
  q.set('inst_id1', params.inst_id1);
  q.set('exchange2', params.exchange2);
  q.set('inst_id2', params.inst_id2);
  q.set('start_ms', String(params.start_ms));
  q.set('end_ms', String(params.end_ms));
  q.set('interval', String(params.interval));
  if (params.sma_period != null) q.set('sma_period', String(params.sma_period));
  q.set('exchange1_fee', String(params.exchange1_fee ?? OPP_DETECTOR_DEFAULT_EXCHANGE1_FEE));
  q.set('exchange2_fee', String(params.exchange2_fee ?? OPP_DETECTOR_DEFAULT_EXCHANGE2_FEE));
  q.set(
    'min_profit_percentage',
    String(params.min_profit_percentage ?? OPP_DETECTOR_DEFAULT_MIN_PROFIT_PERCENTAGE),
  );
  if (params.fill_miss_data != null) q.set('fill_miss_data', String(params.fill_miss_data));
  q.set('use_cache', 'false');

  const res = await apiFetch(`${oppDetectorUrl('/api/opp-detector/spread/detailed')}?${q.toString()}`);
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`opp-detector spread ${res.status}: ${text || res.statusText}`);
  }
  const payload = (await res.json()) as OppCompactDetailedSpreadResponse;
  return {
    data: payload.data.map(oppCompactPointToDetailed),
    total_points: payload.total_points,
    short1_long2_mid_spread_deviation: payload.short1_long2_mid_spread_deviation ?? null,
  };
}

/** Compact wire format from opp-detector `/spread/detailed`. */
interface OppCompactDetailedSpreadPoint {
  tms: number;
  lcl: number;
  lcs: number;
  lms: number;
  lol: number;
  los: number;
  scl: number;
  scs: number;
  sms: number;
  sol: number;
  sos: number;
}

interface OppCompactDetailedSpreadResponse {
  data: OppCompactDetailedSpreadPoint[];
  total_points: number;
  short1_long2_mid_spread_deviation?: DetailedSpreadResponse['short1_long2_mid_spread_deviation'];
}

function oppCompactPointToDetailed(point: OppCompactDetailedSpreadPoint): DetailedSpreadResponse['data'][number] {
  return {
    timestamp: new Date(point.tms).toISOString(),
    bid_price1: 0,
    ask_price1: 0,
    bid_price2: 0,
    ask_price2: 0,
    long1_short2_close_spread: point.lcs,
    long1_short2_open_spread: point.los,
    short1_long2_open_spread: point.sos,
    short1_long2_close_spread: point.scs,
    long1_short2_open_line: point.lol,
    long1_short2_close_line: point.lcl,
    short1_long2_open_line: point.sol,
    short1_long2_close_line: point.scl,
    long1_short2_mid_spread: point.lms,
    short1_long2_mid_spread: point.sms,
    mid_price1: 0,
    mid_price2: 0,
  };
}

function mapOppCandlesToApi(candles: OppL1CandlePoint[]): CandleDataPoint[] {
  return candles.map((candle) => ({
    timestamp: candle.timestamp,
    open: candle.open,
    high: candle.high,
    low: candle.low,
    close: candle.close,
    avg_mid_price: (candle.open + candle.close) / 2,
    tick_count: candle.tick_count,
  }));
}

export async function getOppFundingHistory(params: OppFundingHistoryQuery): Promise<OppFundingHistoryResponse | null> {
  const q = new URLSearchParams();
  q.set('exchange', params.exchange);
  q.set('inst_id', params.inst_id);
  q.set('start_ms', String(params.start_ms));
  q.set('end_ms', String(params.end_ms));
  if (params.interval != null) q.set('interval', String(params.interval));

  const res = await apiFetch(`${oppDetectorUrl('/api/opp-detector/funding/history')}?${q.toString()}`);
  if (res.status === 404) return null;
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`opp-detector funding/history ${res.status}: ${text || res.statusText}`);
  }
  return res.json() as Promise<OppFundingHistoryResponse>;
}

export async function getOppFundingHedge(
  params: OppFundingHedgeQuery,
): Promise<OppFundingHedgeResponse | null> {
  const q = new URLSearchParams({
    exchange1: params.exchange1,
    exchange2: params.exchange2,
    start_ms: String(params.start_ms),
  });
  if (params.end_ms != null) q.set('end_ms', String(params.end_ms));
  if (params.sort_by) q.set('sort_by', params.sort_by);
  if (params.limit != null && params.limit > 0) q.set('limit', String(Math.floor(params.limit)));
  if (params.offset != null && params.offset >= 0) q.set('offset', String(Math.floor(params.offset)));

  const res = await apiFetch(`${oppDetectorUrl('/api/opp-detector/funding/pair-hedge')}?${q.toString()}`);
  if (res.status === 404) return null;
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`opp-detector funding/pair-hedge ${res.status}: ${text || res.statusText}`);
  }
  return res.json() as Promise<OppFundingHedgeResponse>;
}

export async function getOppFundingHedgeUpcoming(
  params: OppFundingHedgeUpcomingQuery,
): Promise<OppFundingHedgeUpcomingResponse | null> {
  const q = new URLSearchParams({
    exchange1: params.exchange1,
    exchange2: params.exchange2,
  });
  if (params.sort_by) q.set('sort_by', params.sort_by);
  if (params.limit != null && params.limit > 0) q.set('limit', String(Math.floor(params.limit)));
  if (params.offset != null && params.offset >= 0) q.set('offset', String(Math.floor(params.offset)));

  const res = await apiFetch(
    `${oppDetectorUrl('/api/opp-detector/funding/pair-hedge-upcoming')}?${q.toString()}`,
  );
  if (res.status === 404) return null;
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`opp-detector funding/pair-hedge-upcoming ${res.status}: ${text || res.statusText}`);
  }
  return res.json() as Promise<OppFundingHedgeUpcomingResponse>;
}

export async function getOppFundingHedgeScreen(
  params: OppFundingHedgeScreenQuery,
): Promise<OppFundingHedgeScreenResponse | null> {
  const q = new URLSearchParams({
    exchanges: params.exchanges.join(','),
  });
  if (params.history_window_secs != null && params.history_window_secs > 0) {
    q.set('history_window_secs', String(Math.floor(params.history_window_secs)));
  }
  if (params.sort_by) q.set('sort_by', params.sort_by);
  if (params.limit != null && params.limit > 0) q.set('limit', String(Math.floor(params.limit)));
  if (params.offset != null && params.offset >= 0) q.set('offset', String(Math.floor(params.offset)));

  const res = await apiFetch(
    `${oppDetectorUrl('/api/opp-detector/funding/pair-hedge-screen')}?${q.toString()}`,
  );
  if (res.status === 404) return null;
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`opp-detector funding/pair-hedge-screen ${res.status}: ${text || res.statusText}`);
  }
  return res.json() as Promise<OppFundingHedgeScreenResponse>;
}

export async function getOppL1Candles(params: OppL1CandlesQuery): Promise<CandleResponse> {
  const q = new URLSearchParams();
  q.set('exchange', params.exchange);
  q.set('inst_id', params.inst_id);
  q.set('start_ms', String(params.start_ms));
  q.set('end_ms', String(params.end_ms));
  if (params.interval != null) q.set('interval', String(params.interval));

  const res = await apiFetch(`${oppDetectorUrl('/api/opp-detector/l1/candles')}?${q.toString()}`);
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`opp-detector l1/candles ${res.status}: ${text || res.statusText}`);
  }
  const payload = (await res.json()) as OppL1CandlesResponse;
  const data = mapOppCandlesToApi(payload.candles ?? []);
  return {
    data,
    statistics: { total_points: data.length },
  };
}

export type OppSpreadPriceDataSource = 'l1' | 'trade';

export interface OppTradePriceSpreadQuery {
  exchange1: string;
  inst_id1: string;
  exchange2: string;
  inst_id2: string;
  start_ms: number;
  end_ms: number;
  interval: number;
}

export interface OppTradePriceSpreadPoint {
  tms: number;
  price1: number;
  price2: number;
  spread: number;
  vol1: number;
  vol2: number;
  count1: number;
  count2: number;
}

export interface OppTradePriceSpreadResponse {
  interval_secs: number;
  total_points: number;
  data: OppTradePriceSpreadPoint[];
}

export interface OppTradeCandlesQuery {
  exchange: string;
  inst_id: string;
  start_ms: number;
  end_ms: number;
  interval?: number;
}

export interface OppTradeVolumeResponse {
  exchange: string;
  inst_id: string;
  query_time_ms: number;
  vol_5m_usd: number;
  vol_10m_usd: number;
  vol_30m_usd: number;
  vol_1h_usd: number;
  vol_4h_usd: number;
  vol_24h_usd: number;
}

export async function getOppTradeVolume(
  exchange: string,
  instId: string,
): Promise<OppTradeVolumeResponse> {
  const q = new URLSearchParams();
  q.set('exchange', exchange);
  q.set('inst_id', instId);

  const res = await apiFetch(`${oppDetectorUrl('/api/opp-detector/trade/volume')}?${q.toString()}`);
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`opp-detector trade/volume ${res.status}: ${text || res.statusText}`);
  }
  return res.json() as Promise<OppTradeVolumeResponse>;
}

interface OppTradeCandlePoint {
  timestamp: string;
  open: number;
  high: number;
  low: number;
  close: number;
  volume_usdt: number;
  trade_count: number;
  bar_count: number;
}

interface OppTradeCandlesResponse {
  candles: OppTradeCandlePoint[];
}

function mapOppTradeCandlesToApi(candles: OppTradeCandlePoint[]): CandleDataPoint[] {
  return candles.map((candle) => ({
    timestamp: candle.timestamp,
    open: candle.open,
    high: candle.high,
    low: candle.low,
    close: candle.close,
    avg_mid_price: (candle.open + candle.close) / 2,
    tick_count: candle.trade_count,
  }));
}

export async function getOppTradePriceSpread(
  params: OppTradePriceSpreadQuery,
): Promise<OppTradePriceSpreadResponse> {
  const q = new URLSearchParams();
  q.set('exchange1', params.exchange1);
  q.set('inst_id1', params.inst_id1);
  q.set('exchange2', params.exchange2);
  q.set('inst_id2', params.inst_id2);
  q.set('start_ms', String(params.start_ms));
  q.set('end_ms', String(params.end_ms));
  q.set('interval', String(params.interval));

  const res = await apiFetch(`${oppDetectorUrl('/api/opp-detector/spread/trade-price')}?${q.toString()}`);
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`opp-detector spread/trade-price ${res.status}: ${text || res.statusText}`);
  }
  return res.json() as Promise<OppTradePriceSpreadResponse>;
}

export async function getOppTradeCandles(params: OppTradeCandlesQuery): Promise<CandleResponse> {
  const q = new URLSearchParams();
  q.set('exchange', params.exchange);
  q.set('inst_id', params.inst_id);
  q.set('start_ms', String(params.start_ms));
  q.set('end_ms', String(params.end_ms));
  if (params.interval != null) q.set('interval', String(params.interval));

  const res = await apiFetch(`${oppDetectorUrl('/api/opp-detector/trade/candles')}?${q.toString()}`);
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`opp-detector trade/candles ${res.status}: ${text || res.statusText}`);
  }
  const payload = (await res.json()) as OppTradeCandlesResponse;
  const data = mapOppTradeCandlesToApi(payload.candles ?? []);
  return {
    data,
    statistics: { total_points: data.length },
  };
}

export async function createOppDetectorWsTicket(): Promise<WsTicketResponse> {
  const path = '/api/opp-detector/ws-ticket';
  const url = oppDetectorUrl(path);
  const res = await apiFetch(url, { method: 'GET' });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`ws ticket ${res.status}: ${text || res.statusText}`);
  }
  return res.json() as Promise<WsTicketResponse>;
}

const toWsBase = (base: string): string => {
  if (!base) {
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    return `${protocol}//${window.location.host}`;
  }
  if (base.startsWith('http://')) return `ws://${base.slice('http://'.length)}`;
  if (base.startsWith('https://')) return `wss://${base.slice('https://'.length)}`;
  if (base.startsWith('/')) {
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    return `${protocol}//${window.location.host}${base}`;
  }
  return base;
};

export function getRealtimeSpreadWsUrl(params: RealtimeSpreadWsParams): string {
  const query = new URLSearchParams({
    exchange1: params.exchange1,
    inst1: params.inst1,
    exchange2: params.exchange2,
    inst2: params.inst2,
    interval: String(params.interval),
    direction: params.direction,
  });
  if (params.ticket) query.set('ticket', params.ticket);
  const wsBase = toWsBase(API_CONFIG.OPP_DETECTOR_PATH_PREFIX);
  const path = '/api/opp-detector/ws/spread-kline';
  return `${wsBase}${path}?${query.toString()}`;
}
